import './index.less';
import * as React from 'react';
import PropTypes from 'prop-types';
import { Button, Table, InputNumber, Form, Upload } from '@beehive/components';
import { TabContent } from '../components';
import { isBlank, showAlert, beforeUploadSuccess } from 'utils';
import classnames from 'classnames';
import { financial } from 'utils/number';
import _ from 'lodash';
import messages from 'messages/Workbench/ProjectDetail';
import { getCooperateAccountsQuoteData, getTranslatedFormItem } from '../../../../utils';
import { UploadFile } from 'modules/types/commons/file';
import { tableColumnWidthToRem } from '@mpc/common/src/utils';

export interface PendingQuoteProps {
  tabKey: string;
}

const TABLE_COLUMNS = [
  { key: 'index', title: 'index', align: 'left', width: 80 },
  { key: 'platform', title: 'superior_resource_id', align: 'center', width: 100 },
  { key: 'account', title: 'account', align: 'left', width: 60 },
  { key: 'remark', title: 'remark', align: 'left', width: 100 },
  { key: 'quote_price', title: 'quote_price_rmb', align: 'right', width: 120 },
];

const FULL_COL_PROPS = {
  itemColProps: { span: 24 },
  itemLabelColProps: { span: 4 },
  itemInputColProps: { span: 20 },
};
export const FormProps = {
  mode: 'edit',
  isLabelVertical: false,
  showClientError: false,
  checkMinHeight: false,
  ...FULL_COL_PROPS,
};
const FormItems = [
  {
    key: 'bid_attachs_blob_ids',
    label: 'bid_attachs',
    childProps: {
      type: 'Upload',
      uploadKey: 'bid_attachs',
      showUploadList: { showRemoveIcon: true },
      multiple: true,
      uploadTips: 'uploadTips',
    },
  },
];
const MAX_SIZE_LIMIT = 1024 * 1024 * 200;

class PendingQuote extends React.Component<PendingQuoteProps, any> {
  private scrollbarRef: React.RefObject<any> = React.createRef();
  public static contextTypes = {
    intl: PropTypes.object,
    prefixCls: PropTypes.string,
    menuKey: PropTypes.string,
    detail: PropTypes.any,

    loadDetail: PropTypes.func,
    projectMove: PropTypes.func,
  };

  constructor(props: PendingQuoteProps, context: any) {
    super(props, context);

    const { intl, detail } = context;
    const form = getCooperateAccountsQuoteData(detail, intl, messages);

    this.state = { form, attachs: [], submitting: false };
  }

  private handleSubmit = () => {
    const { intl, menuKey, detail, loadDetail, projectMove } = this.context;
    const { form = [], attachs = [] } = this.state;
    const params = form.filter((i: any) => !i.isTotal).filter((i: any) => !isBlank(i.quote_price));
    if (params.length <= 0) {
      showAlert({ type: 'error', message: intl.formatMessage(messages.hasNoQuoteAlert) });
      return;
    }
    this.setState({ submitting: true });
    return projectMove(
      {
        id: detail.id,
        pending_quote: {
          is_pass: true,
          project_suppliers_attributes: {
            id: detail.current_project_supplier_id,
            is_quoted: true,
            project_supplier_quotes_attributes: params.map((i: any) => {
              return { cooperate_account_id: i.id, quote_price: i.quote_price };
            }),
            bid_attachs_blob_ids: [...attachs],
          },
        },
      },
      { menuKey },
    ).then((res: any) => {
      showAlert({ type: 'success', message: res.res_msg });
      this.setState({ submitting: false });
      loadDetail();
    }).catch((error: any) => {
      showAlert({ type: 'error', message: typeof error === 'string' ? error : error.message });
      this.setState({ submitting: false });
    });
  }

  private getTotalPrice = (data: any[]) => {
    const total = data.filter((i: any) => !i.isTotal)
      .reduce((prev: number, curr: any) => {
        const price = curr['quote_price'];
        prev = _.add(prev, isBlank(price) ? 0 : +price);
        return prev;
      }, 0);
    return total;
  }

  private handleBeforeUpload = (file: any) => {
    const { intl } = this.context;
    if (file.size && file.size > MAX_SIZE_LIMIT) {
      showAlert({
        type: 'error', message: intl.formatMessage(messages.fileExceededLimit, {
          filename: file.name,
          fileMaxSize: '200M',
        }),
      });
      return false;
    }
    return true;
  }

  private getUploadBody = (file: UploadFile, key: string) => {
    return {
      model: 'SocialMedia::ProjectSupplier',
      filename: file.name,
      attach_type: key,
    };
  }

  private handleUploadChange = (options: any, key: string) => {
    const { attachs = [] } = this.state;
    const { file } = options;
    const blob = _.get(file, 'response.data');
    let newAttaches = [...attachs];
    switch (file.status) {
      case 'done':
        newAttaches.push({ blob_id: blob.blob_id, blob_key: blob.blob_key });
        this.scrollbarRef.current && this.scrollbarRef.current.scrollToBottom();
        break;
      case 'removed':
        newAttaches = newAttaches.filter((a: any) => a.blob_id !== blob.blob_id);
        break;
      default:
        return;
    }
    this.setState({ attachs: newAttaches });
  }

  private handleChange = (key: string, index: number, value: any) => {
    const { form = [] } = this.state;
    const newForm = [...form];
    newForm[index][key] = value;
    newForm[newForm.length - 1]['quote_price'] = this.getTotalPrice(newForm);
    this.setState({ form: newForm });
  }

  private getColumnDefaultRender = () => {
    return (v: any, r: any, i: number) => {
      return isBlank(v) ? r.isTotal ? '' : '--' : v;
    };
  }

  private renderFormItemChild = (itemProps: any, childProps: any) => {
    const { key } = itemProps;
    const { type, ...childPropsRest } = childProps;
    switch (type) {
      case 'Upload': {
        const { uploadKey, ...uploadPropsRest } = childPropsRest;
        return (
          <Upload
            {...uploadPropsRest}
            action="/api/v1/attachments/upload"
            beforeUpload={this.handleBeforeUpload}
            beforeSuccess={beforeUploadSuccess}
            data={(file: UploadFile) => this.getUploadBody(file, uploadKey)}
            onChange={(options: any) => this.handleUploadChange(options, key)}
          />
        );
      }
      default: {
        return <span />;
      }
    }
  }

  private getColumnRender = () => {
    const { intl } = this.context;
    return {
      index: (v: any, r: any, i: number) => {
        return r.isTotal ? intl.formatMessage(messages.quote_price_total) : isBlank(v) ? '--' : v;
      },
      quote_price: (v: any, r: any, i: number) => {
        if (r.isTotal) {
          return isBlank(v) ? '--' : financial(v);
        }
        return (
          <InputNumber
            hideHandle
            min={0}
            precision={2}
            placeholder={intl.formatMessage(messages.inputPlaceholder)}
            value={v}
            formatter={(val: any) => financial(val, true)}
            onChange={(value: any) => this.handleChange('quote_price', i, value)}
          />
        );
      },
    };
  }

  private getColumns = () => {
    const { intl } = this.context;
    const defaultRender = this.getColumnDefaultRender();
    const columRenders = this.getColumnRender();
    return TABLE_COLUMNS.map((item: any) => {
      return {
        ...tableColumnWidthToRem(item),
        dataIndex: item.key,
        title: intl.formatMessage(messages[item.title]),
        render: columRenders[item.key] || defaultRender,
        className: classnames({
          [item.key]: true,
        }),
      };
    });
  }

  private renderBottom = () => {
    const { intl } = this.context;
    const { submitting } = this.state;
    return (
      <Button
        className="pull-right"
        size="large"
        type="primary"
        loading={submitting}
        onClick={this.handleSubmit}
      >
        {intl.formatMessage(messages.submit)}
      </Button>
    );
  }

  public render() {
    const { intl } = this.context;
    const { tabKey } = this.props;
    const { form } = this.state;

    const bottom = this.renderBottom();
    const columns = this.getColumns();

    return (
      <TabContent tabKey={tabKey} bottom={bottom} canScroll ref={this.scrollbarRef}>
        <Table
          columns={columns}
          data={form}
          size="small"
          rowKey="index"
        />
        <Form {...FormProps}>
          {FormItems.map((item: any) => {
            const translatedItem = getTranslatedFormItem(item, intl, messages);
            const { childProps, ...itemProps } = translatedItem;
            return (
              <Form.Item
                {...itemProps}
                key={itemProps.key}
                itemKey={itemProps.formKey || itemProps.key}
                className={itemProps.key}
              >
                {this.renderFormItemChild(itemProps, childProps)}
              </Form.Item>
            );
          })}
        </Form>
      </TabContent>
    );
  }

}

export default PendingQuote;
